import React from 'react';
import { getSnapshot, subscribe } from '../JS/Utils/loadProgress';
import '../Assets/CSS/LoadingScreen.css';

// Keep in step with the `transition: opacity` duration in LoadingScreen.css.
export const LOADING_FADE_MS = 600;

/** The iris, whose hue is the only thing progress does to it.
 *
 *  That hue is published here rather than on the overlay root, and the difference is not
 *  cosmetic: a custom property set on the root is inherited by every descendant, so each
 *  progress tick rewrote a value the two spinning circles below read — the load invalidating
 *  the style of the decoration as it went past, several times a second. The property belongs
 *  on the one element that consumes it.
 */
function LoadingIris() {
  const { fraction } = React.useSyncExternalStore(subscribe, getSnapshot);

  return (
    <div
      className="loadingScreen__core"
      aria-hidden="true"
      style={{ '--load-hue': `${Math.round(fraction * 90)}deg` }}
    />
  );
}

/** The numeral — with the iris's hue, the whole of what follows progress. */
function LoadingPercent() {
  const { fraction } = React.useSyncExternalStore(subscribe, getSnapshot);

  return <div className="loadingScreen__percent">{Math.round(fraction * 100)}%</div>;
}

/**
 * Fullscreen portal overlay shown while the opening scene loads.
 *
 * Colour and numeral are the only progress readings, and each subscribes for itself. This
 * component reads nothing but `finished`, so advancing the load does not re-render the markup
 * beneath it: the tick ring and the sweep are CSS animation on nodes progress never touches,
 * and they hold their speed for the whole load rather than being redrawn along with it. The
 * scene's real asset count drives the two readings, so the screen never claims a number it
 * cannot back up; the iris aperture is deliberately fixed and counts nothing.
 */
export default function LoadingScreen() {
  // Selecting the flag alone keeps this component out of the progress updates: a boolean
  // compares by value, where the snapshot object is a new one on every tick.
  const finished = React.useSyncExternalStore(subscribe, () => getSnapshot().finished);
  const [gone, setGone] = React.useState(false);

  React.useEffect(() => {
    if (!finished) return undefined;

    const timer = setTimeout(() => setGone(true), LOADING_FADE_MS);

    return () => clearTimeout(timer);
  }, [finished]);

  if (gone) return null;

  return (
    <div
      className={`loadingScreen${finished ? ' loadingScreen--done' : ''}`}
      // Inline as well as in the stylesheet: the scene raycasts from
      // clientX/clientY against #canvasEl, so this overlay must never accept a
      // pointer event — including if the stylesheet fails to load.
      style={{ pointerEvents: 'none' }}
      role="status"
      aria-live="polite"
      aria-label="Loading the 3D gallery"
    >
      <div className="loadingScreen__stage">
        <LoadingIris />

        <svg className="loadingScreen__rings" viewBox="0 0 240 240" aria-hidden="true">
          <circle className="loadingScreen__ring loadingScreen__ring--outer" cx="120" cy="120" r="112" />
          <circle className="loadingScreen__ring loadingScreen__ring--mid" cx="120" cy="120" r="96" />
        </svg>

        {/* Each spinning circle gets its own <svg>, and the rotation goes on a <div> WRAPPING
            that <svg>. The wrapper is the whole point of this markup and it is not a style
            preference: a transform animated on an <svg> root is not composited, so it stops dead
            whenever the loader holds the main thread — which it does for seconds at a stretch
            while the opening scene is parsed and its textures decoded. Rotating a <div> around it
            keeps turning through those blocks. Both were measured on this machine in a real GPU
            Chrome; the numbers and the probe are cited in LoadingScreen.css.

            Keep the two out of the shared <svg> above, and keep the rotation off the <svg>. */}
        <div className="loadingScreen__spin loadingScreen__spin--ticks" aria-hidden="true">
          <svg className="loadingScreen__rings" viewBox="0 0 240 240">
            <circle className="loadingScreen__ring--ticks" cx="120" cy="120" r="104" />
          </svg>
        </div>

        <div className="loadingScreen__spin loadingScreen__spin--sweep" aria-hidden="true">
          <svg className="loadingScreen__rings" viewBox="0 0 240 240">
            <circle className="loadingScreen__sweepGlow" cx="120" cy="120" r="104" />
            <circle className="loadingScreen__sweep" cx="120" cy="120" r="104" />
          </svg>
        </div>

        <LoadingPercent />
      </div>
    </div>
  );
}
